import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { Box, Button, Stack, Typography, Divider, selectClasses } from '@mui/material';

import { PaperCard } from 'components/Card';
import { CardAutocomplete, FormProvider, TextField } from 'components/Form';
import { useForm } from 'react-hook-form';
import { AccordionListItem } from 'components/Button';
import { useParams } from 'react-router-dom';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { ImageUpload } from 'components/ImageUpload';
import { useUpdate } from '../api/update';
import { blogShema } from '../validation';
import useYupValidationResolver from 'hook/useYupValidationResolver';
import { getCompanies } from 'features/frameworkAgreements/api/getCompanies';
import { ImageAvatar } from 'components/Avatar';
import { useAuth } from 'lib/auth';

const getDefaultValues = () => ({
  title: '',
  description: '',
  content: '',
  tag: '',
  picture: null,
  creatorID: null,
});

const ItemCard = ({
  Name: name,
  VAT: vatNumber,
  Description: description,
  companyType,
  square_logo_url,
  alreadySelected,
  company_id,
  ...rest
}) => {
  if (alreadySelected && alreadySelected.company_id === company_id) {
    return null;
  }

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={2}
      sx={{ img: { borderRadius: 1, width: 20, height: 20 } }}
      {...rest}
    >
      <ImageAvatar img={square_logo_url || ''} name={name} searchResult />
      <Typography variant="body1" fontWeight={500}>
        {name} ({vatNumber})
      </Typography>
    </Stack>
  );
};

export const Edit = ({blog, refetch}) => {
  const { account } = useAuth();
  const { id } = useParams();
  const [open, setOpen] = useState(false);
  const methods = useForm({
    defaultValues: getDefaultValues(),
    resolver: useYupValidationResolver(blogShema),
  });

  const [ picture, setPicture] = useState(null);
  const [ pictureUploaderOpen, setPictureUploaderOpen] = useState(false);
  const [ pictureURL, setPictureURL ] = useState(null);
  const [ selectedBuyer, setSelectedBuyer ] = useState({ ...blog.Creator, label: blog.Creator.Name });

  const handleBuyerChange = (value) => {
    setSelectedBuyer(value);    
    methods.setValue("creatorID", value?.company_id);
  }

  const { mutateAsync } = useUpdate(refetch);

  const handleCancel = () => {
    setOpen(false);
    if (blog.Creator && blog.Creator.Name) {
      setSelectedBuyer({ ...blog.Creator, label: blog.Creator.Name });
    } else {
      setSelectedBuyer(null);
    }
    methods.reset({ ...blog });
  };

  const handleClose = () => {
    setOpen(false);
    methods.reset({ ...getDefaultValues() });
  };

  const onSubmit = (values) => {
    mutateAsync({
      ...values,
      blogID: id,
    }).then(handleClose);
  };

  useEffect(() => {
    if (!open) {
      methods.reset({ ...blog });
      document.body.style.overflow = '';
    } else if (open) {
      const {
        title,
        description,
        content,
        tag,
        picture_url,
        creator_id,
      } = blog;

      methods.reset({
        title: title,
        description: description,
        content: content,
        tag: tag,
        creatorID: creator_id,
      });

      setPictureURL(picture_url);
      if (picture_url) {
        // The existing picture is only previewed via its URL here (no need
        // to fetch/re-upload it) — a truthy placeholder is enough to satisfy
        // the form's "picture" validation unless the user picks a new image.
        methods.setValue('picture', { existingUrl: picture_url });
      }


      if (blog.Creator && blog.Creator.Name) {
        setSelectedBuyer({ ...blog.Creator, label: blog.Creator.Name });
      } else {
        setSelectedBuyer(null);
      }

      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  useEffect(() => {
    if (picture && picture.file) {
      const url = URL.createObjectURL(picture.file);
      setPictureURL(url);

      methods.setValue("picture", picture);

      return () => {
        URL.revokeObjectURL(url);
      };
    } else {
      setPictureURL(null);
    }
  }, [picture]);

  const EditContent = (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            height: '100%',
            width: '100%',
            background: '#F3F4F6',
            justifyContent: 'center',
            display: 'flex',
            zIndex: (theme) => theme.zIndex.modal,
            alignItems: 'flex-start',
            overflow: 'hidden',
          }}
        >
          <Stack
            sx={{
              position: 'relative',
              width: '100%',
              // height: `calc(100% - 8 * 26.25px)`,
              // marginTop: 26.25,
              height: `calc(100% - 69px)`,
              marginTop: 8.625,
              overflowY: 'auto',
              backgroundColor: '#fff',
            }}
          >
            <FormProvider methods={methods} onSubmit={onSubmit}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <ImageUpload
                  title="Upload blog picture"
                  aspect={388 / 223}
                  uploadImage={() => {}}
                  image={pictureURL || ''}
                  open={pictureUploaderOpen}
                  handleClose={() => setPictureUploaderOpen(false)}
                  setFile={setPicture}
                />
              <PaperCard
                height={69}
                sx={{
                  background: '#fff',
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  width: '100%',
                  px: 4,
                  py: 2,
                  border: 0,
                  borderRadius: 0,
                  boxShadow: 26,
                  display: 'flex',
                  boxSizing: 'border-box',
                  zIndex: (theme) => theme.zIndex.modal + 1,
                }}
              >
                <Stack
                  direction="row"
                  sx={{
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                  }}
                >
                  <Typography>Editing Blog</Typography>
                  <Box
                    sx={{
                      width: 165,
                      display: 'flex',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Button
                      size="medium"
                      onClick={handleCancel}
                      variant="contained"
                      sx={({ palette }) => ({
                        width: 82,
                        boxShadow: 25,
                        color: palette.primary.main,
                        backgroundColor: '#fff',
                      })}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="medium"
                      type="submit"
                      // onClick={onSubmit}
                      variant="contained"
                      color="primary"
                      sx={{ width: 67 }}
                    >
                      Save
                    </Button>
                  </Box>
                </Stack>
              </PaperCard>

              <Stack
                direction="row"
                width="100%"
                minHeight={`calc(100vh - 69px)`}
                height="100%"
                bgcolor={'#fff'}
                divider={<Divider orientation="vertical" flexItem />}
              >
                <Stack width={'50%'} direction="column" px={8} py={4}>
                  <TextField
                    name="content"
                    label="Content"
                    required
                    placeholder="Please enter content"
                    type="text"
                    fullWidth
                    sx={{
                      mb: 2,
                      '& .MuiInputBase-root, .MuiInputBase-input': {
                        p: 0,
                      },
                    }}
                    multiline
                    minRows={2}
                  />
                </Stack>

                <Stack width={'50%'} direction="column" px={8} py={4}>
                  <Button
                    name="picture"
                    size="medium"
                    onClick={() => setPictureUploaderOpen(true)}
                    variant="contained"
                    sx={({ palette }) => ({
                      width: 200,
                      boxShadow: 25,
                      color: palette.primary.dark,
                      backgroundColor: '#fff',
                      mb: picture ? 0.5 : 2,
                    })}
                  >
                    Upload Picture
                  </Button>
                  {picture && (
                    <Typography variant="body2" sx={{ fontSize: "0.75rem", fontWeight: 500, mb: 2}}>
                      File: Blog.png
                    </Typography>
                  )}
                  {!!(methods.formState.errors?.picture) && (
                    <Typography variant="body2" sx={{ fontSize: "0.75rem", fontWeight: 700, mb: 2, mt:-2, color: "#d32f2f"}}>
                      {methods.formState.errors?.picture?.message}
                    </Typography>
                  )}
                  <TextField
                    name="title"
                    label="Title"
                    placeholder="Please enter Blog title"
                    required
                    type="text"
                    sx={{ mb: 2 }}
                    fullWidth
                    slotProps={{ htmlInput: { maxLength: 60 } }}
                  />
                  <TextField
                    name="description"
                    label="Description"
                    placeholder="Please enter Description"
                    required
                    type="text"
                    sx={{ mb: 2 }}
                    fullWidth
                  />
                  <TextField
                    name="tag"
                    label="Tag"
                    placeholder="Please enter tag"
                    required
                    type="text"
                    sx={{ mb: 2 }}
                    fullWidth
                  />
                  <CardAutocomplete
                    label={`Select Buyer`}
                    fullWidth
                    onChange={handleBuyerChange}
                    queryFunction={(searchTerm) => getCompanies('buyer', searchTerm, false)}
                    ItemCard={(props) => <ItemCard companyType={'supplier'} {...props} alreadySelected={selectedBuyer}  />}
                    handleNotFoundButtonClick={null}
                    optionLabelName="Name"
                    companyType="Buyer"
                    skipCreatingShadow
                    value = {selectedBuyer}
                    valueChangable={account.super_admin}
                    changeSelectedValueOnChange={account.super_admin}
                  />
                </Stack>
              </Stack>
              </LocalizationProvider>
            </FormProvider>
          </Stack>
        </Box>
  );

  return (
    <>
      <AccordionListItem onClick={() => setOpen(true)}>Edit</AccordionListItem>
      {open && ReactDOM.createPortal(EditContent, document.body)}
    </>
  );
};
